import React from 'react';

/**
 * Top-level error boundary that prevents the dreaded "white screen of death".
 *
 * The most common cause of a blank page on first load (that fixes itself after
 * 2-3 manual reloads) is a failed JavaScript chunk download on a flaky mobile
 * connection — the app crashes before React can render anything, and with no
 * boundary the user is left staring at a white page.
 *
 * This boundary:
 *  - Detects chunk/dynamic-import load failures and does ONE automatic, clean
 *    reload (cache-busted) — the same manual reload that currently "fixes" it,
 *    now done for the user automatically.
 *  - For any other render error, shows a minimal branded fallback with a
 *    "Prøv igen" button instead of a blank screen.
 */

const CHUNK_ERROR_RE = /(Loading chunk|Loading CSS chunk|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|dynamically imported module)/i;
const RELOAD_FLAG = '__enkel_chunk_reload__';

function isChunkLoadError(error) {
  const msg = (error && (error.message || String(error))) || '';
  return CHUNK_ERROR_RE.test(msg) || error?.name === 'ChunkLoadError';
}

export default class RootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error) {
    // If a chunk failed to load, auto-recover with a single clean reload.
    // Guard with sessionStorage so we never get stuck in a reload loop.
    if (isChunkLoadError(error)) {
      let alreadyReloaded = false;
      try {
        alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG) === '1';
        sessionStorage.setItem(RELOAD_FLAG, '1');
      } catch {
        // sessionStorage unavailable (private mode etc.) — fall through to button
      }
      if (!alreadyReloaded) {
        window.location.reload();
      }
    }
  }

  componentDidMount() {
    // A successful mount means chunks loaded fine — clear the guard so a future
    // genuine chunk failure can auto-recover again.
    try {
      sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      // ignore
    }
  }

  handleRetry = () => {
    try {
      sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      // ignore
    }
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    // While the auto-reload for a chunk error is in flight, render nothing
    // (avoids a flash of the fallback before the page reloads).
    if (this.state.isChunkError) {
      let alreadyReloaded = false;
      try {
        alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG) === '1';
      } catch {
        alreadyReloaded = false;
      }
      if (!alreadyReloaded) {
        return (
          <div className="fixed inset-0 flex items-center justify-center bg-[#F8F8F8]">
            <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin" />
          </div>
        );
      }
    }

    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[#F8F8F8] px-6">
        <div className="text-center max-w-sm">
          <img
            src="https://base44.app/api/apps/691da83d30e4689f56b0df07/files/mp/public/691da83d30e4689f56b0df07/77ba4fb0d_d9c1ec595_Enkel_LOGO-281.png"
            alt="enkel energi"
            width="160"
            className="h-12 w-auto object-contain mx-auto mb-6"
          />
          <h1 className="text-lg font-bold text-[#1F2E33] mb-2" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>
            Noget gik galt
          </h1>
          <p className="text-sm text-[#5E7B8C] mb-6">
            Siden kunne ikke indlæses korrekt. Prøv at indlæse den igen.
          </p>
          <button
            onClick={this.handleRetry}
            className="inline-flex items-center justify-center px-6 h-11 rounded-xl bg-[#2F424D] hover:bg-[#1F2E33] text-[#F7F3EA] font-semibold transition-colors"
          >
            Prøv igen
          </button>
        </div>
      </div>
    );
  }
}